import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetFormSubmissionById } from '../../services/react-query/form_submissions/useGetFormSubmissionById';
import { Avatar, Box, Button, Chip, Container, Snackbar, Stack, Typography } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { Loading } from '../../uikit';
import { FormSubmission, FormSubmissionField } from '../../types/FormSubmission';
import FieldInput from '../../components/common/FieldsSection/FieldInput';
import { FieldValue } from '../../types/FieldCommon';
import { AxiosError } from 'axios';
import { humanizeDate } from '../../utility/dateUtils';
import { useUpdateFormSubmission } from '../../services/react-query/form_submissions/useUpdateFormSubmission';
import { AttachmentOwnerType } from '../../types/Attachment';
import ConfirmationModal from '../../components/ConfirmationModal';
import FormAttachmentsSection from './FormAttachmentsSection';
import {
  buttonRowSx,
  dueDatePillSx,
  dueDateStackSx,
  fieldRowSx,
  formHeaderContainerSx,
  formHeaderLayoutSx,
  formHeaderSx,
  formHeaderTitleStackSx,
  formHeaderProps,
  formLogoContainerSx,
  formLogoSx,
  formMainContainerSx,
  headerSubtitleProps,
  sectionSx,
} from './formStyles';
import _ from 'lodash';
import { useGetAttachmentsByOwner } from '../../services/react-query/attachments';

const isMissingRequiredValue = (value: FieldValue) => {
  if (_.isNil(value)) return true;

  if (Array.isArray(value)) return _.isEmpty(value);
  if (typeof value === 'string') return !value.trim();

  return false;
};

interface FormHeaderProps {
  dueDate: string;
  eventName: string;
  formTemplateName: string;
  organizationLogoUrl?: string;
  organizationName: string;
}

interface FormFieldRowProps {
  field: FormSubmissionField;
  helperText?: string;
  showError: boolean;
  updateFieldValue: (value: FieldValue) => void;
}

function FormHeader({ dueDate, eventName, formTemplateName, organizationLogoUrl, organizationName }: FormHeaderProps) {
  return (
    <>
      <Box sx={formHeaderSx}>
        <Container maxWidth={false} sx={formHeaderContainerSx}>
          <Box sx={formHeaderLayoutSx}>
            <Box sx={formLogoContainerSx}>
              {organizationLogoUrl ? (
                <Box component="img" src={organizationLogoUrl} alt={organizationName} sx={formLogoSx} />
              ) : (
                <Avatar>{organizationName?.charAt(0)?.toUpperCase()}</Avatar>
              )}
            </Box>

            <Stack spacing={0.2} sx={formHeaderTitleStackSx}>
              <Typography {...formHeaderProps}>{formTemplateName}</Typography>
              <Typography {...headerSubtitleProps}>{eventName}</Typography>
            </Stack>

            <Stack spacing={0.6} direction={{ xs: 'column', lg: 'row' }} sx={dueDateStackSx}>
              <Typography {...headerSubtitleProps}>Submit by</Typography>
              <Chip icon={<CalendarMonth />} sx={dueDatePillSx} label={dueDate} />
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
}

function FormFieldRow({ field, helperText, showError, updateFieldValue }: FormFieldRowProps) {
  return (
    <Stack key={field.uuid} spacing={0.5} sx={fieldRowSx}>
      <Typography fontWeight="bold">
        {field.label}
        {field.required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        )}
      </Typography>
      <FieldInput
        field={field}
        showLabel={false}
        error={showError && field.type !== 'checkbox'}
        helperText={field.type !== 'checkbox' ? helperText : undefined}
        updateFieldValue={updateFieldValue}
      />
      {showError && field.type === 'checkbox' && (
        <Typography variant="caption" color="error">
          {helperText}
        </Typography>
      )}
      {field.description && (
        <Typography color="textSecondary" variant="caption">
          {field.description}
        </Typography>
      )}
    </Stack>
  );
}

export default function FormSubmit() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [formSubmission, setFormSubmission] = useState<FormSubmission | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [attachmentError, setAttachmentError] = useState<boolean>(false);
  const { data: formSubmissionData, error: fetchError } = useGetFormSubmissionById({ uuid });
  const { mutate: updateFormSubmission } = useUpdateFormSubmission({ uuid });

  useEffect(() => {
    if (!formSubmissionData) return;
    setFormSubmission(formSubmissionData);
  }, [formSubmissionData]);

  const errorStatus = (fetchError as AxiosError | null)?.response?.status;

  useEffect(() => {
    if (errorStatus === 403) {
      navigate(`/form/authenticate/${uuid}`);
    } else if (errorStatus === 307) {
      // this happens when verification code has expired
      const errorData = (fetchError as AxiosError<{ uuid: string }>).response?.data;
      navigate(`/form/get-otp/${errorData?.uuid}`);
    }
  }, [errorStatus, fetchError, navigate, uuid]);

  const { data: submissionAttachments } = useGetAttachmentsByOwner({
    type: AttachmentOwnerType.FORM_SUBMISSION,
    uuid: formSubmission?.uuid ?? '',
    enabled: formSubmission?.event_form_template?.form_template_attachment_requirement === 'required',
  });

  if (!formSubmission) return <Loading />;

  const { fields, uuid: submissionUuid, event_form_template: eventFormTemplate } = formSubmission;
  const eventName = eventFormTemplate?.event_display_name || eventFormTemplate?.event_name;
  const dueDate = humanizeDate(eventFormTemplate?.close_date);

  const missingRequiredFieldUuids = _.map(
    _.filter(fields, (field) => field.required && isMissingRequiredValue(field.value)),
    'uuid'
  );
  const isFormValid = missingRequiredFieldUuids.length === 0;

  const handleFieldValueUpdate = (fieldUuid: string, value: FieldValue) =>
    setFormSubmission({
      ...formSubmission,
      fields: _.map(formSubmission.fields, (field) => (field.uuid === fieldUuid ? { ...field, value } : field)),
    });

  const handleSave = () => updateFormSubmission(formSubmission);

  const handleSubmit = () => {
    updateFormSubmission({ ...formSubmission, submit: true });
    setConfirmationOpen(false);
    setTimeout(() => {
      window.location.href = 'https://schedulink.io';
    }, 2000);
  };

  const handleSubmitClick = () => {
    if (!isFormValid) {
      setShowValidation(true);
      return;
    }

    if (eventFormTemplate.form_template_attachment_requirement === 'required' && !submissionAttachments?.length) {
      setAttachmentError(true);
      return;
    }

    setConfirmationOpen(true);
  };

  return (
    <Box>
      <title>ScheduLink | Studio Management Software for Volume Photo Studios</title>
      <FormHeader
        dueDate={dueDate}
        eventName={eventName}
        formTemplateName={eventFormTemplate.form_template_name}
        organizationLogoUrl={eventFormTemplate.organization_logo_url}
        organizationName={eventFormTemplate.organization_name}
      />

      <Container component="main" maxWidth="sm" sx={formMainContainerSx}>
        <Stack spacing={2}>
          {
            eventFormTemplate.form_template_instructions &&
            <Box sx={sectionSx}>
              <Typography variant="h6">Instructions</Typography>
              <Typography>{eventFormTemplate.form_template_instructions}</Typography>
            </Box>
          }
          <Box sx={sectionSx}>
            {_.map(fields, (field) => {
              const showError = showValidation && missingRequiredFieldUuids.includes(field.uuid);

              return (
                <FormFieldRow
                  key={field.uuid}
                  field={field}
                  helperText={showError ? 'This field is required' : undefined}
                  showError={showError}
                  updateFieldValue={(value) => handleFieldValueUpdate(field.uuid, value)}
                />
              );
            })}
          </Box>

          <FormAttachmentsSection
            ownerType={AttachmentOwnerType.FORM_TEMPLATE}
            ownerUuid={eventFormTemplate.form_template_uuid}
            title="Files to Download"
            emptyText="No files are attached for download."
          />

          {eventFormTemplate.form_template_attachment_requirement !== 'na' && (
            <FormAttachmentsSection
              ownerType={AttachmentOwnerType.FORM_SUBMISSION}
              ownerUuid={submissionUuid}
              title="Files to Upload"
              emptyText="No files have been uploaded yet."
              editable={true}
            />
          )}

          <Box sx={{ ...sectionSx, ...buttonRowSx }}>
            <Button variant="outlined" onClick={handleSave}>
              Save Draft
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClick}
            >
              Submit
            </Button>
          </Box>
        </Stack>
      </Container>

      <ConfirmationModal
        text="Once the form is submitted it cannot be modified without contacting the studio. Are you sure you want to submit this form?"
        isOpen={confirmationOpen}
        onConfirm={handleSubmit}
        onCancel={() => setConfirmationOpen(false)}
      />

      <Snackbar open={attachmentError} onClose={() => setAttachmentError(false)} message="At least 1 attachment is required before submitting." />
    </Box>
  );
}
